import React, { useEffect, useState } from "react";
import PanelDashboardLayout from "./PanelDashboardLayout";
import { getAllBatchesViaAdmin, getAllTransactions } from "../../utils/Api";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // filter state
  const [batchFilter, setBatchFilter] = useState(""); // batch filter state
  const [batches, setBatches] = useState([]);

  const fetchTransactions = async () => {
    try {
      const response = await getAllTransactions();
      setTransactions(response);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBatches = async () => {
    try {
      const response = await getAllBatchesViaAdmin();
      setBatches(response.batches);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };
  useEffect(() => {
    fetchTransactions();
    fetchAllBatches();
  }, []);

  // Filter transactions based on filter input and batch selection
  const filteredTransactions = transactions.filter((txn) => {
    const search = filter.toLowerCase();
    const matchesText =
      txn.studentName?.toLowerCase().includes(search) ||
      txn.batchName?.toLowerCase().includes(search);
    const matchesBatch = !batchFilter || txn.batchName === batchFilter;
    return matchesText && matchesBatch;
  });

  // CSV download helper
  const downloadCSV = () => {
    if (!filteredTransactions.length) return;
    const headers = [
      "Student Name",
      "Batch Name",
      "Amount Paid",
      "Payment ID",
      "Payment Date",
    ];
    const rows = filteredTransactions.map((txn) => [
      txn.studentName || "",
      txn.batchName || "",
      txn.amountPaid || "",
      txn.transactionId || "",
      txn.paymentDate
        ? (() => {
            const d = new Date(txn.paymentDate);
            const day = d.getDate().toString().padStart(2, "0");
            const month = (d.getMonth() + 1).toString().padStart(2, "0");
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
          })()
        : "-",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    let filename = "transactions.csv";
    if (!batchFilter) {
      filename = "All_batches_transaction.csv";
    } else {
      // Remove spaces and special chars from batch name for filename
      const safeBatch = batchFilter.replace(/[^a-zA-Z0-9_-]/g, "_");
      filename = `${safeBatch}_transactions.csv`;
    }
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return (
    <PanelDashboardLayout>
      <div className="max-w-5xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-8 text-gray-800">Transactions</h1>
        {/* Show total number of filtered transactions */}
        <div className="mb-2 text-gray-700 font-medium flex items-center gap-4">
          <span>Total: {filteredTransactions.length}</span>
          <button
            className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded shadow transition"
            onClick={downloadCSV}
            disabled={!filteredTransactions.length}
          >
            Download CSV
          </button>
        </div>
        {/* Filter inputs */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-end">
          <input
            type="text"
            className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-64 transition"
            placeholder="Filter by student and batch"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="border border-gray-300 px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-48 transition"
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
          >
            <option value="">All Batches</option>
            {batches.map((batch) => (
              <option key={batch._id || batch.name} value={batch.batchName}>
                {batch.batchName}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <p className="text-gray-600 text-center">No transactions found.</p>
        ) : (
          <div className="overflow-x-auto">
            <div className="bg-white rounded-xl shadow-lg p-4">
              <table className="min-w-full rounded overflow-hidden">
                <thead className="bg-blue-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b font-semibold text-gray-700">
                      Student Name
                    </th>
                    <th className="px-4 py-3 border-b font-semibold text-gray-700">
                      Batch Name
                    </th>
                    <th className="px-4 py-3 border-b font-semibold text-gray-700">
                      Amount Paid
                    </th>
                    <th className="px-4 py-3 border-b font-semibold text-gray-700">
                      Payment ID
                    </th>
                    <th className="px-4 py-3 border-b font-semibold text-gray-700">
                      Payment Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((txn, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-blue-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 border-b">{txn.studentName}</td>
                      <td className="px-4 py-3 border-b">{txn.batchName}</td>
                      <td className="px-4 py-3 border-b text-green-600 font-semibold">
                        ₹{txn.amountPaid}
                      </td>
                      <td className="px-4 py-3 border-b text-xs text-gray-600">
                        {txn.transactionId}
                      </td>
                      <td className="px-4 py-3 border-b">
                        {txn.paymentDate
                          ? (() => {
                              const d = new Date(txn.paymentDate);
                              const day = d
                                .getDate()
                                .toString()
                                .padStart(2, "0");
                              const month = (d.getMonth() + 1)
                                .toString()
                                .padStart(2, "0");
                              const year = d.getFullYear();
                              return `${day}/${month}/${year}`;
                            })()
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PanelDashboardLayout>
  );
};

export default Transactions;
